/**
 * UI event trail for diagnosing React error #185 (Maximum update depth exceeded).
 *
 * Production #185s on iOS are thrown from inside a React event handler, so the
 * error boundary never sees them and the stack holds only React internals. This
 * keeps the last few input/focus/composition events (type and target, never
 * values) so window.onerror can report which field and which event sequence
 * drove the loop.
 */

interface TrailEntry {
  /** ms since the trail entry before it */
  dt: number;
  type: string;
  target: string;
  inputType?: string;
  composing?: boolean;
}

const MAX_ENTRIES = 30;
const EVENT_TYPES = [
  'focusin', 'focusout', 'beforeinput', 'input', 'change',
  'compositionstart', 'compositionend', 'click', 'touchend', 'selectionchange',
];

const _trail: TrailEntry[] = [];
const _times: number[] = [];
let _lastTime = 0;
let _installed = false;

export function describeElement(el: EventTarget | Element | null): string {
  if (!el || !(el instanceof Element)) {
    return el === document ? 'document' : String(el);
  }
  let d = el.tagName.toLowerCase();
  if (el.id) d += '#' + el.id;
  const cls = typeof el.className === 'string' ? el.className.trim().split(/\s+/)[0] : '';
  if (cls) d += '.' + cls;
  const name = el.getAttribute('name');
  if (name) d += '[name=' + name + ']';
  const placeholder = el.getAttribute('placeholder');
  if (placeholder) d += '[placeholder=' + placeholder.slice(0, 30) + ']';
  return d;
}

function record(e: Event): void {
  const now = performance.now();
  const entry: TrailEntry = {
    dt: _lastTime ? Math.round(now - _lastTime) : 0,
    type: e.type,
    target: describeElement(e.type === 'selectionchange' ? document.activeElement : e.target),
  };
  const ie = e as InputEvent;
  if (ie.inputType) entry.inputType = ie.inputType;
  if (ie.isComposing) entry.composing = true;
  _lastTime = now;
  _trail.push(entry);
  _times.push(now);
  if (_trail.length > MAX_ENTRIES) {
    _trail.shift();
    _times.shift();
  }
}

export function installUiEventTrail(): void {
  if (_installed || typeof document === 'undefined') return;
  _installed = true;
  for (const type of EVENT_TYPES) {
    document.addEventListener(type, record, { capture: true, passive: true });
  }
}

/** Summary for an error report: the focused element, recent events, and events per type in the last second. */
export function getUiEventTrailReport(): string {
  const now = performance.now();
  const lastSecond: Record<string, number> = {};
  for (let i = 0; i < _trail.length; i++) {
    if (now - _times[i] <= 1000) {
      lastSecond[_trail[i].type] = (lastSecond[_trail[i].type] || 0) + 1;
    }
  }
  return JSON.stringify({
    active: describeElement(document.activeElement),
    sinceLastEventMs: _lastTime ? Math.round(now - _lastTime) : null,
    lastSecond,
    trail: _trail,
  });
}

/** True for the "Maximum update depth exceeded" error, minified or not. */
export function isUpdateDepthError(msg: unknown): boolean {
  const s = String(msg);
  return s.includes('error #185') || s.includes('Maximum update depth exceeded');
}
